import {
  BUILDING_META,
  MAX_LEVEL,
  THRESHOLDS,
  TOWN_HALL_LEAD,
  formatMetricValue,
  type BuildingId,
  type BuildingProgress,
} from "@engine";
import { buildingArt, harbourArt, wallThumbArt, type Art } from "../../art/design/village.ts";
import { svgUrl } from "../design/textures.ts";
import { SCENIC_META, type ScenicId } from "../scenic.ts";
import type { Session } from "../state.ts";
import { $, hide, show } from "./dom.ts";

const LOCK_SVG =
  '<svg viewBox="0 0 16 18" width="16" height="18" aria-hidden="true"><rect x="2" y="8" width="12" height="9" rx="2" fill="#6b5a43"/><path d="M4.5 8V5.5a3.5 3.5 0 0 1 7 0V8" fill="none" stroke="#6b5a43" stroke-width="2.2"/></svg>';

function setPanelArt(art: Art | null, faded: boolean): void {
  const image = $("panel-art") as HTMLImageElement;
  if (!art) {
    hide(image);
    image.removeAttribute("src");
    return;
  }
  image.src = svgUrl(art.svg);
  image.classList.toggle("unbuilt", faded);
  show(image);
}

const compact = (value: number): string => (value >= 1_000 ? `${value / 1_000}k` : String(value));

/** Short label under each ladder step: what reaching that level takes. */
export function ladderLabels(id: BuildingId): string[] {
  switch (id) {
    case "townHall":
      return THRESHOLDS.townHall.map((n) => (n === 0 ? "Start" : `${n} goal${n === 1 ? "" : "s"}`));
    case "barracks":
      return [...THRESHOLDS.barracks.applications.map((n) => `${n} app${n === 1 ? "" : "s"}`), "Interview", "Offer"];
    case "goldMine":
      return THRESHOLDS.goldMine.map((n) => `£${n.toLocaleString("en-GB")}`);
    case "crystalTankRevenue":
    case "crystalTankViralz":
    case "crystalTankTikwave":
    case "crystalTankGoGrow":
    case "crystalTankRihla":
      return THRESHOLDS.crystalTank.map((n) => `$${n}`);
    case "lab":
      return THRESHOLDS.lab.map((n) => `${n} lesson${n === 1 ? "" : "s"}`);
    case "workshop":
      return ["Repo", "Deploy", "MVP live", "1st user", "Paying"];
    case "watchtower":
      return [
        ...THRESHOLDS.watchtower.posts.map((n) => `${n} post${n === 1 ? "" : "s"}`),
        ...THRESHOLDS.watchtower.followers.map((n) => `${compact(n)} fans`),
      ];
    case "walls":
      return THRESHOLDS.wallsHpBands.map((n) => `${n} HP`);
    case "builderHut":
      return THRESHOLDS.builderHut.map((n) => `${n} day${n === 1 ? "" : "s"}`);
    default: {
      const neverId: never = id;
      throw new Error(`Unhandled building: ${neverId}`);
    }
  }
}

/** Heading over the current value, matched to what the number means. */
function valueLabel(id: BuildingId): string {
  switch (BUILDING_META[id].valueKind) {
    case "gbp":
    case "usd":
      return "This month";
    case "hp":
      return "Wall HP";
    case "stage":
      return "Stage";
    default:
      return id === "townHall" ? "Milestones" : "So far";
  }
}

/** Plain-English source of real progress for the cap note. */
const REAL_THING: Partial<Record<BuildingId, string>> = {
  goldMine: "real income",
  crystalTankRevenue: "real MRR",
  crystalTankViralz: "real MRR",
  crystalTankTikwave: "real MRR",
  crystalTankGoGrow: "real MRR",
  crystalTankRihla: "real MRR",
  lab: "real lessons",
  barracks: "real applications",
  workshop: "real shipping",
  watchtower: "real posts and followers",
  builderHut: "real daily actions",
  walls: "real bug fixes",
  townHall: "real milestones",
};

/** Lower-case noun used in the cap note ("this mine", "this tank"). */
const THIS_NOUN: Partial<Record<BuildingId, string>> = {
  goldMine: "this mine",
  crystalTankRevenue: "this tank",
  crystalTankViralz: "this tank",
  crystalTankTikwave: "this tank",
  crystalTankGoGrow: "this tank",
  crystalTankRihla: "this tank",
  lab: "the Lab",
  barracks: "the Barracks",
  workshop: "the Workshop",
  watchtower: "the Watchtower",
  builderHut: "the Builder Hut",
};

function capFor(id: BuildingId, townHallLevel: number): number {
  if (id === "townHall" || id === "walls") {
    return MAX_LEVEL;
  }
  return Math.min(MAX_LEVEL, townHallLevel + TOWN_HALL_LEAD);
}

function renderLadder(building: BuildingProgress, cap: number): void {
  const list = $("panel-pips");
  list.replaceChildren();
  const labels = ladderLabels(building.id);
  for (let step = 1; step <= MAX_LEVEL; step += 1) {
    const item = document.createElement("li");
    const pip = document.createElement("span");
    pip.className = "pip";
    let state: string;
    if (step <= building.level) {
      state = step === building.level ? "done current" : "done";
    } else if (step > cap) {
      state = "locked";
    } else if (step === building.level + 1) {
      state = "next";
    } else {
      state = "future";
    }
    item.className = state;
    if (state === "locked") {
      pip.innerHTML = LOCK_SVG;
    } else {
      pip.textContent = String(step);
    }
    const caption = document.createElement("span");
    caption.textContent = labels[step - 1] ?? "";
    item.setAttribute(
      "aria-label",
      `Level ${step}: ${caption.textContent}${state === "locked" ? " (locked by the Keep)" : ""}`,
    );
    item.append(pip, caption);
    list.append(item);
  }
  const reached = Math.max(0, Math.min(MAX_LEVEL, building.level) - 1);
  $("panel-ladder-fill").style.width = `${Math.round((reached / (MAX_LEVEL - 1)) * 100)}%`;
}

function nextLine(building: BuildingProgress): { next: string; toGo: string } {
  const target = building.nextThreshold;
  if (target === null || building.level >= MAX_LEVEL) {
    return { next: "Max level reached", toGo: "" };
  }
  const nextLevel = building.level + 1;
  if (typeof target === "number") {
    const label = formatMetricValue(building.id, target);
    const current = typeof building.value === "number" ? building.value : null;
    const gap = current === null ? null : Math.max(0, target - current);
    return {
      next: `Next: Level ${nextLevel} at ${label}`,
      toGo: gap === null || gap === 0 ? "" : `${formatMetricValue(building.id, gap)} to go`,
    };
  }
  return { next: `Next: Level ${nextLevel}`, toGo: String(target) };
}

export function renderBuildingPanel(session: Session, id: BuildingId): void {
  const building = session.result.buildings.find((item) => item.id === id);
  if (!building) {
    return;
  }
  paintPanel(building, session);
}

export function paintPanel(building: BuildingProgress, session: Session): void {
  const meta = BUILDING_META[building.id];
  const townHall = session.result.state.levels.townHall;
  const cap = capFor(building.id, townHall);
  setPanelArt(building.id === "walls" ? wallThumbArt() : buildingArt(building.id), false);
  $("panel-title").textContent = meta.name;
  $("panel-level").textContent = `Level ${building.level}`;
  $("panel-bot").textContent = `${meta.bot} bot`;
  $("panel-metric").textContent = meta.metric;
  $("panel-value-label").textContent = valueLabel(building.id);
  $("panel-value").textContent = formatMetricValue(building.id, building.value);
  const related = relatedMissing(building.id, session.missing);
  $("panel-source").textContent =
    related.length > 0 ? "Not in your dashboard yet" : session.source === "seed" ? "Seed data" : "Live";
  $("panel-source").title = related.length > 0 ? `Missing from dashboard.json: ${related.join(", ")} (treated as 0)` : "";

  const capTag = $("panel-cap-tag");
  if (cap < MAX_LEVEL) {
    capTag.textContent = `Cap L${cap}`;
    show(capTag);
  } else {
    hide(capTag);
  }
  show($("panel-ladder"));

  const { next, toGo } = nextLine(building);
  $("panel-next").textContent = next;
  $("panel-togo").textContent = toGo;
  $("panel-bar").style.width = `${Math.round(building.progressToNext * 100)}%`;
  renderLadder(building, cap);

  const real = REAL_THING[building.id] ?? "real numbers";
  const noun = THIS_NOUN[building.id] ?? "it";
  let note: string;
  if (building.capped) {
    note = `Your numbers earn Level ${building.rawLevel}, but nothing can pass the Keep + 1. Raise the Keep (Level ${townHall}) to unlock it.`;
  } else if (cap < MAX_LEVEL) {
    note = `Keep is Level ${townHall}, so ${noun} can reach Level ${cap} for now. Levels move only with ${real}; loot can’t buy them.`;
  } else {
    note = `Levels move only with ${real}; loot can’t buy them.`;
  }
  $("panel-cap").textContent = note;

  show($("building-panel"));
}

export function renderScenicPanel(id: ScenicId): void {
  const meta = SCENIC_META[id];
  setPanelArt(harbourArt(), false);
  $("panel-title").textContent = meta.name;
  $("panel-level").textContent = "Scenic";
  $("panel-bot").textContent = "Village landmark";
  $("panel-metric").textContent = "No bot yet";
  $("panel-value-label").textContent = "Now";
  $("panel-value").textContent = "—";
  $("panel-source").textContent = "";
  $("panel-next").textContent = "Not levelled by a bot";
  $("panel-togo").textContent = "";
  $("panel-bar").style.width = "0%";
  hide($("panel-ladder"));
  hide($("panel-cap-tag"));
  $("panel-cap").textContent = meta.blurb;
  show($("building-panel"));
}

export function closePanel(): void {
  hide($("building-panel"));
}

function relatedMissing(id: BuildingId, missing: string[]): string[] {
  const prefixes: Partial<Record<BuildingId, string[]>> = {
    townHall: ["planMilestonesHit"],
    barracks: ["applicationsSent", "interviewsLanded", "offersReceived"],
    goldMine: ["sideHustleGbpPerMonth"],
    crystalTankRevenue: ["appMrrUsd.revenue"],
    crystalTankViralz: ["appMrrUsd.viralz"],
    crystalTankTikwave: ["appMrrUsd.tikwave"],
    crystalTankGoGrow: ["appMrrUsd.gogrow"],
    crystalTankRihla: ["appMrrUsd.rihla"],
    lab: ["lessonsDone"],
    workshop: ["builder."],
    watchtower: ["socialPosts", "socialFollowers"],
    walls: ["bugsFixed"],
    builderHut: ["dailyActionsTotal", "dailyActionsDoneYesterday"],
  };
  const keys = prefixes[id] ?? [];
  return missing.filter((item) => keys.some((key) => item.startsWith(key) || item === key));
}
